import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, canAccessAccounting } from '../context/AuthContext';
import { updateUserPreferences, updateUser } from '../services/api';
import { UNITS_CHANGE_EVENT, readUnitSystem } from '../services/unitPreference';
import { useTranslation } from 'react-i18next';
import { TIMEZONES } from '../i18n';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PersonIcon from '@mui/icons-material/Person';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import StraightenIcon from '@mui/icons-material/Straighten';
import CheckIcon from '@mui/icons-material/Check';
import SettingsIcon from '@mui/icons-material/Settings';

import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import RouterIcon from '@mui/icons-material/Router';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsNotFixedIcon from '@mui/icons-material/GpsNotFixed';
import KeyIcon from '@mui/icons-material/Key';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import HandymanIcon from '@mui/icons-material/Handyman';
import CompressIcon from '@mui/icons-material/Compress';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import MapIcon from '@mui/icons-material/Map';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ForestIcon from '@mui/icons-material/Forest';

import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

const DRAWER_WIDTH = 180;
const COLLAPSED_WIDTH = 56;
const FOOTER_HEIGHT = 56;
// On phones the section tabs move out of the app bar onto their own full-width
// row underneath it, so they aren't squeezed between the logo and the account
// icons. This is that row's height, added to the app bar's 64px.
const MOBILE_TABS_HEIGHT = 44;

const topTabs = [
  { labelKey: 'nav.equipment', path: '/equipment' },
  { labelKey: 'nav.operations', path: '/operations' },
  { labelKey: 'nav.environmental', path: '/environmental' },
  { labelKey: 'nav.accounting', path: '/accounting' },
];

const equipmentMenuItems = [
  {
    label: 'Machines',
    path: '/equipment/machines',
    icon: <PrecisionManufacturingIcon />
  },
  {
    label: 'Omnibox',
    path: '/equipment/omnibox',
    icon: <RouterIcon />
  },
  {
    label: 'GPS Systems',
    path: '/equipment/machines/gps-health',
    icon: <GpsFixedIcon />
  },
  {
    label: 'GPS Rovers',
    path: '/equipment/gps-rovers',
    icon: <GpsNotFixedIcon />  // Place Holder Icon, will be updated with a better fitting icon later //
  },
  {
    label: 'GPS Licenses',
    path: '/equipment/gps-licenses',
    icon: <KeyIcon />
  },
  {
    label: 'Load Testing',
    path: '/equipment/data-loggers',
    icon: <FlashOnIcon />
  },
  {
    label: 'Equipment Map',
    path: '/equipment/map',
    icon: <MapIcon />
  }
];

const operationsMenuItems = [
  {
    label: 'Job Sites',
    path: '/operations/job-sites',
    icon: <PlaceIcon />
  },
  {
    label: 'Tools',
    path: '/operations/tools',
    icon: <HandymanIcon />
  },
  {
    label: 'Load Testing',
    path: '/operations/load-test',
    icon: <CompressIcon />
  },
];

const accountingMenuItems = [
  {
    label: 'TiPs Converter',
    path: '/accounting/tips-converter',
    icon: <AttachMoneyIcon />
  },
];

const environmentalMenuItems = [
  {
    label: 'Coming Soon',
    path: '/environmental',
    icon: <ForestIcon />
  },
];

function MainLayout({ children, darkMode, toggleDarkMode }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // const { user, onLogout, onUpdateUser } = useAuth();
  const auth = useAuth() || {};
  const user = auth.user || null;
  const onLogout = auth.onLogout || (() => { });
  const onUpdateUser = auth.onUpdateUser || (() => { });

  const location = useLocation();
  const navigate = useNavigate();

  const visibleTabs = topTabs.filter(
    (tab) => tab.path !== '/accounting' || canAccessAccounting(user?.role)
  );

  const showDrawer = !(location.pathname.startsWith('/feedback') || location.pathname.startsWith('/contact') || location.pathname.startsWith('/about'));

  // Collapsible side menu — only on the Load Testing dashboard, where every
  // pixel of width matters. Elsewhere the drawer stays fixed at full width.
  const collapsibleNav = location.pathname.startsWith('/operations/load-test/live');
  const [navCollapsed, setNavCollapsed] = useState(true);
  const navWidth = collapsibleNav && navCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Phones/small tablets: the side menu becomes an overlay opened from the app
  // bar, so the page gets the full width instead of losing 180px to the drawer.
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close the overlay after navigating, so the page isn't left behind it.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Determine active top tab index based on visible tabs
  const getActiveTab = () => {
    const idx = visibleTabs.findIndex((tab) => location.pathname.startsWith(tab.path));
    if (location.pathname.startsWith('/feedback')) return -1;
    return idx === -1 ? 0 : idx;
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [unitsAnchor, setUnitsAnchor] = useState(null);
  const [homePageAnchor, setHomePageAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [regionAnchor, setRegionAnchor] = useState(null);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [countryAnchor, setCountryAnchor] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [businessUnitAnchor, setBusinessUnitAnchor] = useState(null);
  const [availableBusinessUnitsList, setAvailableBusinessUnitsList] = useState([]);

  // Country-switch confirmation dialog state.
  // Switching country forces the admin to pick a BU + region in that country before committing,
  // because BUs and regions are country-scoped and the current values are almost certainly
  // invalid in the new country.
  const [pendingCountry, setPendingCountry] = useState(null);
  const [pendingCountryBuList, setPendingCountryBuList] = useState([]);
  const [pendingCountryBu, setPendingCountryBu] = useState('');
  const [pendingCountryRegionList, setPendingCountryRegionList] = useState([]);
  const [pendingCountryRegion, setPendingCountryRegion] = useState('');
  const [pendingCountryBusy, setPendingCountryBusy] = useState(false);

  const [units, setUnits] = useState(readUnitSystem);

  // Follow changes made elsewhere (the account value applied at sign-in, the
  // Load Testing toggle) so the Settings toggle never shows a stale choice.
  useEffect(() => {
    const onChange = () => setUnits(readUnitSystem());
    window.addEventListener(UNITS_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(UNITS_CHANGE_EVENT, onChange);
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSignOut = () => {
    handleMenuClose();
    onLogout();
  };

  const handleHomePageClick = (event) => {
    setHomePageAnchor(event.currentTarget);
  };

  const handleHomePageClose = () => {
    setHomePageAnchor(null);
  };

  const handleHomePageChange = async (path) => {
    handleHomePageClose();
    handleMenuClose();
    try {
      await updateUserPreferences(user.id, { homePage: path });
      onUpdateUser({ homePage: path });
    } catch (e) {
      console.error('Failed to save homepage preference', e);
    }
  };

  const homePageOptions = [
    { label: t('nav.equipment'), path: '/equipment' },
    { label: t('nav.operations'), path: '/operations/job-sites' },
    { label: t('nav.environmental'), path: '/environmental' },
    ...(canAccessAccounting(user?.role) ? [{ label: t('nav.accounting'), path: '/accounting' }] : []),
  ];

  const handleUnitsClick = (event) => {
    setUnitsAnchor(event.currentTarget);
  };

  const handleUnitsClose = () => {
    setUnitsAnchor(null);
  };

  const handleUnitsChange = (unit) => {
    setUnits(unit);
    localStorage.setItem('gps-units', unit);
    window.dispatchEvent(new Event('gps-units-changed'));
    handleUnitsClose();
    handleMenuClose();
  };

  const handleSettingsOpen = () => {
    handleMenuClose();
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleLanguageChange = async (lang) => {
    try {
      await updateUserPreferences(user.id, { language: lang });
      onUpdateUser({ language: lang });
    } catch (e) {
      console.error('Failed to save language preference', e);
    }
  };

  const handleTimezoneChange = async (tz) => {
    try {
      await updateUserPreferences(user.id, { timezone: tz });
      onUpdateUser({ timezone: tz });
    } catch (e) {
      console.error('Failed to save timezone preference', e);
    }
  };

  const handleHomePageSelect = async (path) => {
    try {
      await updateUserPreferences(user.id, { homePage: path });
      onUpdateUser({ homePage: path });
    } catch (e) {
      console.error('Failed to save homepage preference', e);
    }
  };

  const handleUnitsSelect = (unit) => {
    setUnits(unit);
    localStorage.setItem('gps-units', unit);
    window.dispatchEvent(new Event('gps-units-changed'));
  };

  useEffect(() => {
    const regionParams = new URLSearchParams();
    if (user?.businessUnit) regionParams.set('businessUnit', user.businessUnit);
    if (user?.country) regionParams.set('country', user.country);
    const regionQs = regionParams.toString();
    fetch(`/api/regions${regionQs ? `?${regionQs}` : ''}`)
      .then(r => r.json())
      .then(d => setAvailableRegions(d.regions || []))
      .catch(() => { });
    fetch(`/api/business-units${user?.country ? `?country=${encodeURIComponent(user.country)}` : ''}`)
      .then(r => r.json())
      .then(d => setAvailableBusinessUnitsList(d.businessUnits || []))
      .catch(() => { });
  }, []);

  const handleRegionClick = (event) => {
    setRegionAnchor(event.currentTarget);
  };

  const handleRegionClose = () => {
    setRegionAnchor(null);
  };

  // Region, country and business unit set the user's data scope, so these pickers go
  // through the admin user-edit endpoint (PATCH /api/users/:id), which enforces who may
  // change them. /api/user/preferences refuses them.
  const handleRegionChange = async (region) => {
    handleRegionClose();
    handleMenuClose();
    try {
      await updateUser(user.id, { region });
      onUpdateUser({ region });
    } catch (e) {
      console.error('Failed to save region preference', e);
    }
  };

  const handleCountryClick = (event) => {
    setCountryAnchor(event.currentTarget);
    if (availableCountries.length === 0) {
      fetch('/api/countries').then(r => r.json()).then(d => setAvailableCountries(d.countries || [])).catch(() => { });
    }
  };

  const handleCountryClose = () => {
    setCountryAnchor(null);
  };

  const handleCountryChange = async (country) => {
    handleCountryClose();
    handleMenuClose();
    // No-op when picking the same country the user already has.
    if (!country || country === user?.country) return;
    // Open the BU/region selection dialog. We don't persist anything until the admin
    // picks both and confirms — cancelling effectively reverts the country.
    setPendingCountry(country);
    setPendingCountryBu('');
    setPendingCountryBuList([]);
    setPendingCountryRegion('');
    setPendingCountryRegionList([]);
    try {
      const r = await fetch(`/api/business-units?country=${encodeURIComponent(country)}`);
      const d = await r.json();
      setPendingCountryBuList(d.businessUnits || []);
    } catch (e) {
      console.error('Failed to load business units for', country, e);
    }
  };

  // Refresh region list whenever the admin picks a BU in the pending-country dialog.
  useEffect(() => {
    if (!pendingCountry || !pendingCountryBu) {
      setPendingCountryRegionList([]);
      setPendingCountryRegion('');
      return;
    }
    const params = new URLSearchParams();
    params.set('businessUnit', pendingCountryBu);
    params.set('country', pendingCountry);
    fetch(`/api/regions?${params.toString()}`)
      .then(r => r.json())
      .then(d => {
        setPendingCountryRegionList(d.regions || []);
        setPendingCountryRegion('');
      })
      .catch(() => { });
  }, [pendingCountry, pendingCountryBu]);

  const handlePendingCountryCancel = () => {
    setPendingCountry(null);
    setPendingCountryBu('');
    setPendingCountryBuList([]);
    setPendingCountryRegion('');
    setPendingCountryRegionList([]);
  };

  const handlePendingCountryConfirm = async () => {
    if (!pendingCountry || !pendingCountryBu || !pendingCountryRegion) return;
    setPendingCountryBusy(true);
    try {
      await updateUser(user.id, {
        country: pendingCountry,
        businessUnit: pendingCountryBu,
        region: pendingCountryRegion,
      });
      onUpdateUser({
        country: pendingCountry,
        businessUnit: pendingCountryBu,
        region: pendingCountryRegion,
      });

      // The user-menu's region/BU submenus are populated by a mount-only effect, so they
      // won't refresh on their own when country changes. Push the lists we already have.
      setAvailableRegions(pendingCountryRegionList);
      setAvailableBusinessUnitsList(pendingCountryBuList);

      handlePendingCountryCancel();
    } catch (e) {
      console.error('Failed to save country/BU change', e);
    } finally {
      setPendingCountryBusy(false);
    }
  };

  const handleBusinessUnitClick = (event) => {
    setBusinessUnitAnchor(event.currentTarget);
  };

  const handleBusinessUnitClose = () => {
    setBusinessUnitAnchor(null);
  };

  const handleBusinessUnitChange = async (businessUnit) => {
    handleBusinessUnitClose();
    handleMenuClose();
    try {
      await updateUser(user.id, { businessUnit, region: 'General' });
      onUpdateUser({ businessUnit, region: 'General' });
      const buRegionParams = new URLSearchParams();
      if (businessUnit) buRegionParams.set('businessUnit', businessUnit);
      if (user?.country) buRegionParams.set('country', user.country);
      const buRegionQs = buRegionParams.toString();
      fetch(`/api/regions${buRegionQs ? `?${buRegionQs}` : ''}`)
        .then(r => r.json())
        .then(d => setAvailableRegions(d.regions || []))
        .catch(() => { });
    } catch (e) {
      console.error('Failed to save business unit preference', e);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top App Bar - Black Control Center Style */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: darkMode ? '#000000' : '#fafafa',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: darkMode ? '0 4px 8px rgba(255, 255, 255, 0.15)' : '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <Toolbar disableGutters sx={{ minHeight: 64 }}>
          {/* Menu button — mobile only, opens the drawer as an overlay */}
          {isMobile && showDrawer && (
            <IconButton
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
              sx={{ ml: 0.5, color: darkMode ? '#ffffff' : 'rgba(0,0,0,0.87)' }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {/* MTS Logo - Centered over sidebar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: { xs: 'auto', md: DRAWER_WIDTH },
              flexShrink: 0,
            }}
          >
            <Box
              // component={Link}
              // to="/dashboard"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'left',
                pl: { xs: 1, md: 3 },
                // cursor: 'pointer',
              }}
            >
              {/* <Box
                component="img"
                src={location.pathname.startsWith('/qr-feedback') ? "/menard-logo.png" : darkMode ? '/white_forge_PNG.png' : '/black_forge_PNG.png'}
                // src='forge-just-logo.png'
                alt="Menard Technical Services"
                sx={{
                  height: 32,
                  width: 'auto',
                  filter: location.pathname.startsWith('/qr-feedback') ? darkMode ? 'none': 'invert(1)' : 'none',
                }}
              /> */}
              <Box
                component='img'
                src='/Forge_Black.png'
                alt='Forge'
                sx={{
                  height: { xs: 32, md: 40 },
                  width: 'auto',
                  filter: darkMode ? 'invert(1)' : 'none'
                }}
              />
            </Box>
          </Box>


          {/* Top Tabs — desktop only; the mobile row lives under the Toolbar */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: isMobile ? 'none' : 'flex',
              justifyContent: 'center'
            }}
          >

            <Tabs
              // When the contacts page is selected,
              // We don't want the previously selected tab (Top Menus) to remain highlighted, so getActiveTab() returns -1

              //Converting -1 into false allows us to avoid error messages in the console
              value={getActiveTab() === -1 ? false : getActiveTab()}
              onChange={handleTabChange}
              // Scrollable on mobile so every section stays reachable; MUI
              // forbids `centered` together with the scrollable variant.
              centered={!isMobile}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{
                flexGrow: 1,
                minWidth: 0,
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
                '& .MuiTabs-flexContainer': {
                  gap: { xs: 0, md: 3 },
                  justifyContent: { xs: 'flex-start', md: 'center' },
                },
                '& .MuiTab-root': {
                  fontWeight: 400,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  textTransform: 'none',
                  minHeight: 64,
                  minWidth: 'auto',
                  color: darkMode ? '#737373' : 'rgba(0,0,0,0.87)',
                  transition: 'all 0.2s',
                  px: { xs: 1, md: 2 },
                  gap: 1,
                  '&:hover': {
                    color: darkMode ? '#a3a3a3' : 'rgba(0,0,0,0.6)',
                  },
                  '&.Mui-selected': {
                    color: darkMode ? '#ffffff' : 'rgba(0,0,0,0.87)',
                    fontWeight: 600,
                    textShadow: 'none',
                  },
                  '& .MuiTab-iconWrapper': {
                    marginRight: 1,
                  },
                },
              }}
            >
              {visibleTabs.map((tab) => (
                <Tab
                  key={tab.labelKey}
                  label={t(tab.labelKey)}
                  component={Link}
                  to={tab.path}
                />
              ))}
            </Tabs>
          </Box>

          {/* Dark Mode Toggle */}
          <Box
            sx={{
              width: { xs: 'auto', md: DRAWER_WIDTH - 4 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              pr: 1,
              ml: 'auto',
            }}
          >

            <IconButton onClick={toggleDarkMode} sx={{ color: '#737373', '&:hover': { color: '#ffffff' } }}>
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            {/* Settings Menu */}
            <IconButton
              onClick={handleMenuOpen}
              sx={{ color: '#737373', ml: 1, '&:hover': { color: '#ffffff' } }}
            >
              <PersonIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              disableScrollLock={true}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: darkMode ? {
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #262626',
                } : {}
              }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" sx={{ color: '#a3a3a3' }} />
                </ListItemIcon>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{user?.fullName || user?.username}</Typography>
                  <Typography variant="caption" sx={{ color: '#a3a3a3', textTransform: 'capitalize', display: 'block' }}>
                    {user?.role === 'bu_manager' ? 'Business Unit Admin' : user?.role}
                  </Typography>
                  {user?.businessUnit && (
                    <Typography variant="caption" sx={{ color: '#a3a3a3', display: 'block' }}>
                      {user.businessUnit}
                    </Typography>
                  )}
                  {user?.region && (
                    <Typography variant="caption" sx={{ color: '#a3a3a3', display: 'block' }}>
                      {user.region}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              <Divider />
              {['admin', 'bu_manager'].includes(user?.role) && (
                <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/users'); }}>
                  <ListItemIcon>
                    <ManageAccountsIcon fontSize="small" sx={{ color: '#a3a3a3' }} />
                  </ListItemIcon>
                  <ListItemText>{t('nav.manageUsers')}</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={handleSignOut}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: '#a3a3a3' }} />
                </ListItemIcon>
                <ListItemText>{t('nav.signOut')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleSettingsOpen}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" sx={{ color: '#a3a3a3' }} />
                </ListItemIcon>
                <ListItemText>{t('nav.settings')}</ListItemText>
              </MenuItem>
              {false && (
                <MenuItem onClick={handleRegionClick}>
                  <ListItemIcon>
                    <AddLocationAltIcon fontSize="small" sx={{ color: '#a3a3a3' }} />
                  </ListItemIcon>
                  <ListItemText>Region</ListItemText>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {user?.region || 'None'}
                  </Typography>
                </MenuItem>
              )}
              {false && (
                <MenuItem onClick={handleBusinessUnitClick}>
                  <ListItemIcon>
                    <BusinessIcon fontSize="small" sx={{ color: '#a3a3a3' }} />
                  </ListItemIcon>
                  <ListItemText>Business Unit</ListItemText>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {user?.businessUnit || 'None'}
                  </Typography>
                </MenuItem>
              )}
              {false && (
                <MenuItem onClick={handleCountryClick}>
                  <ListItemIcon>
                    <PublicIcon fontSize="small" sx={{ color: '#a3a3a3' }} />
                  </ListItemIcon>
                  <ListItemText>Country</ListItemText>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {user?.country || 'None'}
                  </Typography>
                </MenuItem>
              )}
            </Menu>

            {/* Settings Dialog */}
            <Dialog
              open={settingsOpen}
              onClose={handleSettingsClose}
              maxWidth="xs"
              fullWidth
              PaperProps={{ sx: darkMode ? { backgroundColor: '#141414' } : {} }}
            >
              <DialogTitle>{t('settings.title')}</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('settings.language')}</InputLabel>
                    <Select
                      value={user?.language || 'en'}
                      label={t('settings.language')}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                      <MenuItem value="en">{t('common.english')}</MenuItem>
                      <MenuItem value="fr">{t('common.french')}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('settings.timezone')}</InputLabel>
                    <Select
                      value={user?.timezone || 'America/New_York'}
                      label={t('settings.timezone')}
                      onChange={(e) => handleTimezoneChange(e.target.value)}
                    >
                      {TIMEZONES.map(tz => (
                        <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('settings.homePage')}</InputLabel>
                    <Select
                      value={user?.homePage || ''}
                      label={t('settings.homePage')}
                      onChange={(e) => handleHomePageSelect(e.target.value)}
                    >
                      {homePageOptions.map(option => (
                        <MenuItem key={option.path} value={option.path}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('settings.units')}
                    </Typography>
                    <ToggleButtonGroup
                      value={units}
                      exclusive
                      fullWidth
                      size="small"
                      onChange={(e, val) => { if (val) handleUnitsSelect(val); }}
                    >
                      <ToggleButton value="imperial">{t('settings.imperial')}</ToggleButton>
                      <ToggleButton value="metric">{t('settings.metric')}</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleSettingsClose}>{t('common.close')}</Button>
              </DialogActions>
            </Dialog>

            {/* Region Submenu */}
            <Menu
              anchorEl={regionAnchor}
              open={Boolean(regionAnchor)}
              onClose={handleRegionClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: darkMode ? { backgroundColor: '#1a1a1a', border: '1px solid #262626' } : {}
              }}
            >
              {[...availableRegions, ...(!availableRegions.includes('General') ? ['General'] : [])].map((region) => (
                <MenuItem key={region} onClick={() => handleRegionChange(region)}>
                  <ListItemIcon>
                    {user?.region === region ? <CheckIcon fontSize="small" sx={{ color: '#0ea5e9' }} /> : null}
                  </ListItemIcon>
                  <ListItemText>{region}</ListItemText>
                </MenuItem>
              ))}
            </Menu>

            {/* Country Submenu */}
            <Menu
              anchorEl={countryAnchor}
              open={Boolean(countryAnchor)}
              onClose={handleCountryClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: darkMode ? { backgroundColor: '#1a1a1a', border: '1px solid #262626' } : {}
              }}
            >
              {availableCountries.map((country) => (
                <MenuItem key={country} onClick={() => handleCountryChange(country)}>
                  <ListItemIcon>
                    {user?.country === country ? <CheckIcon fontSize="small" sx={{ color: '#0ea5e9' }} /> : null}
                  </ListItemIcon>
                  <ListItemText>{country}</ListItemText>
                </MenuItem>
              ))}
            </Menu>

            {/* Business Unit Submenu */}
            <Menu
              anchorEl={businessUnitAnchor}
              open={Boolean(businessUnitAnchor)}
              onClose={handleBusinessUnitClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: darkMode ? { backgroundColor: '#1a1a1a', border: '1px solid #262626' } : {}
              }}
            >
              {availableBusinessUnitsList.map((bu) => (
                <MenuItem key={bu} onClick={() => handleBusinessUnitChange(bu)}>
                  <ListItemIcon>
                    {user?.businessUnit === bu ? <CheckIcon fontSize="small" sx={{ color: '#0ea5e9' }} /> : null}
                  </ListItemIcon>
                  <ListItemText>{bu}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>

        {/* Section tabs row — phones only */}
        {isMobile && (
          <Box sx={{ height: MOBILE_TABS_HEIGHT, borderTop: darkMode ? '1px solid #1a1a1a' : '1px solid rgba(0,0,0,0.08)' }}>
            <Tabs
              value={getActiveTab() === -1 ? false : getActiveTab()}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minHeight: MOBILE_TABS_HEIGHT,
                '& .MuiTabs-indicator': { backgroundColor: '#0ea5e9', height: 3 },
                '& .MuiTab-root': {
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  minHeight: MOBILE_TABS_HEIGHT,
                  px: 2,
                  color: darkMode ? '#737373' : 'rgba(0,0,0,0.6)',
                  '&.Mui-selected': { color: darkMode ? '#ffffff' : 'rgba(0,0,0,0.87)', fontWeight: 700 },
                },
              }}
            >
              {visibleTabs.map((tab) => (
                <Tab key={tab.labelKey} label={t(tab.labelKey)} component={Link} to={tab.path} />
              ))}
            </Tabs>
          </Box>
        )}
      </AppBar>

      {/* Country-switch BU selector — admin must pick a BU in the new country, or the change is reverted. */}
      <Dialog
        open={Boolean(pendingCountry)}
        onClose={pendingCountryBusy ? undefined : handlePendingCountryCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Choose Business Unit & Region</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Switching to <strong>{pendingCountry}</strong> requires selecting a business unit and region
              in that country. The change is cancelled if you don't pick both.
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Business Unit</InputLabel>
              <Select
                value={pendingCountryBu}
                label="Business Unit"
                onChange={(e) => setPendingCountryBu(e.target.value)}
              >
                {pendingCountryBuList.map((bu) => (
                  <MenuItem key={bu} value={bu}>{bu}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {pendingCountryBuList.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No business units available for {pendingCountry}.
              </Typography>
            )}
            <FormControl size="small" fullWidth disabled={!pendingCountryBu}>
              <InputLabel>Region</InputLabel>
              <Select
                value={pendingCountryRegion}
                label="Region"
                onChange={(e) => setPendingCountryRegion(e.target.value)}
              >
                {[...pendingCountryRegionList, ...(!pendingCountryRegionList.includes('General') ? ['General'] : [])].map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {pendingCountryBu && pendingCountryRegionList.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No specific regions for {pendingCountryBu} — select "General".
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePendingCountryCancel} disabled={pendingCountryBusy}>Cancel</Button>
          <Button
            onClick={handlePendingCountryConfirm}
            variant="contained"
            disabled={!pendingCountryBu || !pendingCountryRegion || pendingCountryBusy}
          >
            {pendingCountryBusy ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sidebar Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isMobile ? mobileNavOpen : showDrawer}
        onClose={() => setMobileNavOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : navWidth,
          flexShrink: 0,
          transition: 'width 0.2s',
          '& .MuiDrawer-paper': {
            width: isMobile ? DRAWER_WIDTH : navWidth,
            boxSizing: 'border-box',
            top: isMobile ? 64 + MOBILE_TABS_HEIGHT : 64,
            height: isMobile
              ? `calc(100% - ${64 + MOBILE_TABS_HEIGHT}px - ${FOOTER_HEIGHT}px)`
              : `calc(100% - 64px - ${FOOTER_HEIGHT}px)`,
            backgroundColor: darkMode ? '#000000' : '#ffffff',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
            transition: 'width 0.2s',
          },
        }}
      >
        <Box sx={{ overflow: 'auto', pt: 2 }}>
          <List>
            {(location.pathname.startsWith('/operations')
              ? operationsMenuItems
              : location.pathname.startsWith('/environmental')
                ? environmentalMenuItems
                : location.pathname.startsWith('/accounting')
                  ? accountingMenuItems
                  : equipmentMenuItems
            ).map((item) => (
              <ListItem key={item.label} disablePadding sx={{ pl: 1, pr: 1, mb: 1.5 }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={
                    location.pathname === item.path ||
                    (location.pathname.startsWith(item.path + '/') &&
                      !equipmentMenuItems.some(
                        (other) => other.path !== item.path && other.path.startsWith(item.path + '/') && (location.pathname === other.path || location.pathname.startsWith(other.path + '/'))
                      ))
                  }
                  sx={{
                    py: 0.25,
                    pl: collapsibleNav && navCollapsed ? 0.5 : 1,
                    pr: 0.5,
                    borderRadius: 1,
                    minHeight: 0,
                    '&.Mui-selected': {
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0284c7',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  {collapsibleNav && navCollapsed ? (
                    <Tooltip title={item.label} placement="right" arrow>
                      <ListItemIcon sx={{ minWidth: 0, mx: 'auto', py: 0.5, color: 'inherit' }}>
                        {item.icon}
                      </ListItemIcon>
                    </Tooltip>
                  ) : (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 400, fontSize: '0.85rem' }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        {/* Collapse toggle — Load Testing dashboard only */}
        {collapsibleNav && (
          <Box sx={{ mt: 'auto', p: 1 }}>
            <Tooltip title={navCollapsed ? 'Expand menu' : 'Collapse menu'} placement="right" arrow>
              <IconButton
                onClick={() => setNavCollapsed(v => !v)}
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'secondary.dark' },
                }}
              >
                {navCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: showDrawer && !isMobile ? `${navWidth}px` : 0,
          transition: 'margin-left 0.2s',
          mt: isMobile ? `${64 + MOBILE_TABS_HEIGHT}px` : '64px',
          mb: `${FOOTER_HEIGHT}px`,
          p: { xs: 1.5, md: 3 },
          minHeight: isMobile
            ? `calc(100vh - ${64 + MOBILE_TABS_HEIGHT}px - ${FOOTER_HEIGHT}px)`
            : `calc(100vh - 64px - ${FOOTER_HEIGHT}px)`,
          // On phones the pages lay out as a single scrolling column that can run taller than the
          // viewport. A fixed height here would let that overflow spill past the bottom margin and
          // under the fixed footer, hiding the last row of content, so let the box grow instead.
          // height: isMobile
          //   ? 'auto'
          //   : `calc(100vh - 64px - ${FOOTER_HEIGHT}px)`,
          height: 'auto',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/background.jpeg)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            filter: darkMode ? 'none' : 'invert(1) brightness(1.08)',
            zIndex: -1,
          },
        }}
      >
        {children}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: FOOTER_HEIGHT,
          backgroundColor: darkMode ? '#0a0a0a' : '#fafafa',
          borderTop: darkMode ? '1px solid #1a1a1a' : 'none',
          boxShadow: darkMode ? 'none' : '0 -2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 1, sm: 3 },
          justifyContent: 'center',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        {/* Menard Logo - Left */}
        <Box
          sx={{
            width: { xs: 'auto', md: DRAWER_WIDTH },
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src="/Menard-Vertical.png"
            alt="Menard"
            sx={{
              height: { xs: 30, md: 40 },
              // The blue brandmark is illegible on the dark footer — render it white there
              filter: darkMode ? 'brightness(0) invert(1)' : 'none',
            }}
          />
        </Box>

        {/* Center Links */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 4 }, flex: 1, justifyContent: 'center', minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: darkMode ? '#ffffff' : 'rgba(0,0,0,0.87)',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { color: darkMode ? '#a3a3a3' : 'rgba(0,0,0,0.6)' }
            }}
          >
            About
          </Typography>
          <Typography
            variant="body2"
            component={Link}
            to='/contact'
            sx={{
              color: darkMode ? '#ffffff' : 'rgba(0,0,0,0.87)',
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': { color: darkMode ? '#a3a3a3' : 'rgba(0,0,0,0.6)' }
            }}
          >
            Contact
          </Typography>
          <Typography
            variant="body2"
            component={Link}
            to='/feedback'
            sx={{
              color: darkMode ? '#ffffff' : 'rgba(0,0,0,0.87)',
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': { color: darkMode ? '#a3a3a3' : 'rgba(0,0,0,0.6)' }
            }}
          >
            Feedback
          </Typography>
          <Typography
            variant="body2"
            component="a"
            href='https://geotecdev.github.io/menard_technique_gallery/'
            sx={{
              color: darkMode ? '#ffffff' : 'rgba(0,0,0,0.87)',
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': { color: darkMode ? '#a3a3a3' : 'rgba(0,0,0,0.6)' }
            }}
          >
            Discover
          </Typography>
        </Box>

        {/* Version - Right (no room for it beside the links on a phone) */}
        <Box
          sx={{
            width: DRAWER_WIDTH,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Typography variant="body2" sx={{ color: darkMode ? '#525252' : 'rgba(0,0,0,0.4)' }}>
            © 2026 v1.0.27
          </Typography>
        </Box>

      </Box>
    </Box>
  );
}

export default MainLayout;
